import api from 'api/api.config';
import { buildQueryParams } from 'helpers/api/build-query-params';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';

import type { CollectionResponse } from 'api/market-view-api';

const getBasePath = (userId: string, clientId: string | number): string =>
  `/${userId}/clients/${clientId}/aid-models`;

export interface AidModelsListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
}

export const getAidModels = async (
  userId: string,
  clientId: string | number,
  params: AidModelsListParams = {},
): Promise<CollectionResponse> => {
  const { pageSize, pageIndex = 0, sortBy, filters } = params;
  const basePath = getBasePath(userId, clientId);

  const response = await api.get(basePath, {
    params: buildQueryParams({
      pageSize,
      pageIndex,
      sortBy,
      filters,
      searchTerms: undefined,
      forceSearch: undefined,
    }),
  });

  const totalCount = response.headers['x-total-count'];
  const pageSizeHeader = response.headers['x-page-size'];
  const pageCount = response.headers['x-page-count'];

  const numPages = Math.ceil(
    Number.parseInt(totalCount, 10) /
      Number.parseInt(String(pageSizeHeader || pageSize || '10'), 10),
  );

  return {
    data: response.data,
    pagination: {
      page: Number.parseInt(pageCount, 10) || pageIndex,
      pageSize: Number.parseInt(pageSizeHeader, 10) || pageSize || 10,
      pageCount: numPages,
      totalRecords: Number.parseInt(totalCount, 10) || 0,
    },
  };
};

export const getAidModel = async (
  userId: string,
  clientId: string | number,
  modelId: string | number,
): Promise<unknown> => {
  const response = await api.get(`${getBasePath(userId, clientId)}/${modelId}`);

  return response.data;
};

export const createAidModel = async (
  userId: string,
  clientId: string | number,
  data: unknown,
): Promise<unknown> => {
  const response = await api.post(
    getBasePath(userId, clientId),
    JSON.stringify(data),
  );

  return response.data;
};

export const updateAidModel = async (
  userId: string,
  clientId: string | number,
  modelId: string | number,
  data: unknown,
): Promise<unknown> => {
  const response = await api.put(
    `${getBasePath(userId, clientId)}/${modelId}`,
    JSON.stringify(data),
  );

  return response.data;
};

export const deleteAidModel = async (
  userId: string,
  clientId: string | number,
  modelId: string | number,
): Promise<void> => {
  await api.delete(`${getBasePath(userId, clientId)}/${modelId}`);
};

export const getAidModelDataset = async (
  userId: string,
  modelId: string | number,
): Promise<unknown> => {
  const response = await api.get(`/${userId}/aid-models/${modelId}/dataset`);

  return response.data;
};

export const createAidModelDataset = async (
  userId: string,
  modelId: string | number,
  sourceTableName: string,
): Promise<unknown> => {
  const response = await api.post(
    `/${userId}/aid-models/${modelId}/dataset`,
    JSON.stringify({ sourceTableName }),
  );

  return response.data;
};

export const getAidSourceTables = async (userId: string): Promise<unknown> => {
  const response = await api.get(`/${userId}/aid-source-tables`);

  return response.data;
};
